import { AnalysisResults } from '@/types/notes';
import jsPDF from 'jspdf';

export interface JTBDFramework {
  functionalJobs: string[];
  emotionalJobs: string[];
  socialJobs: string[];
  forces: {
    pushForces: string[];
    pullForces: string[];
    anxieties: string[];
    habits: string[];
  };
  outcomes: string[];
  recommendations: string[];
}

export const mapAnalysisToJTBD = (results: AnalysisResults): JTBDFramework => {
  // Extract functional jobs from themes and insights
  const functionalJobs = results.themes.filter(theme => 
    theme.toLowerCase().includes('task') || 
    theme.toLowerCase().includes('process') || 
    theme.toLowerCase().includes('workflow') ||
    theme.toLowerCase().includes('function')
  ).concat(
    results.insights.filter(insight => 
      insight.toLowerCase().includes('accomplish') ||
      insight.toLowerCase().includes('complete') ||
      insight.toLowerCase().includes('achieve')
    )
  );

  // Extract emotional jobs from emotions and insights
  const emotionalJobs = results.emotions.concat(
    results.insights.filter(insight => 
      insight.toLowerCase().includes('feel') ||
      insight.toLowerCase().includes('emotion') ||
      insight.toLowerCase().includes('confidence') ||
      insight.toLowerCase().includes('satisfaction')
    )
  );

  // Extract social jobs from insights that mention others
  const socialJobs = results.insights.filter(insight => 
    insight.toLowerCase().includes('team') ||
    insight.toLowerCase().includes('colleague') ||
    insight.toLowerCase().includes('share') ||
    insight.toLowerCase().includes('collaborate') ||
    insight.toLowerCase().includes('status') ||
    insight.toLowerCase().includes('reputation')
  );

  // Map pain points to forces that push users away from current solution
  const pushForces = results.painPoints;

  // Extract pull forces from opportunities
  const pullForces = results.opportunities || [];

  // Extract anxieties from pain points that mention worry, concern, fear
  const anxieties = results.painPoints.filter(pain => 
    pain.toLowerCase().includes('worry') ||
    pain.toLowerCase().includes('concern') ||
    pain.toLowerCase().includes('fear') ||
    pain.toLowerCase().includes('uncertain') ||
    pain.toLowerCase().includes('risk')
  );

  // Extract habits from insights about current behaviors
  const habits = results.insights.filter(insight => 
    insight.toLowerCase().includes('currently') ||
    insight.toLowerCase().includes('usual') ||
    insight.toLowerCase().includes('tend to') ||
    insight.toLowerCase().includes('habit') ||
    insight.toLowerCase().includes('routine')
  );

  return {
    functionalJobs: functionalJobs.length > 0 ? functionalJobs : ['Complete core tasks efficiently'],
    emotionalJobs: emotionalJobs.length > 0 ? emotionalJobs : ['Feel confident and in control'],
    socialJobs: socialJobs.length > 0 ? socialJobs : ['Maintain professional relationships'],
    forces: {
      pushForces,
      pullForces,
      anxieties: anxieties.length > 0 ? anxieties : [],
      habits: habits.length > 0 ? habits : []
    },
    outcomes: results.opportunities || [],
    recommendations: results.recommendations
  };
};

export const exportToJTBD = (
  results: AnalysisResults, 
  isMultiInterview: boolean = false, 
  interviewNames: string[] = []
) => {
  try {
    console.log('Starting JTBD PDF export...');
    const jtbdData = mapAnalysisToJTBD(results);
    const timestamp = new Date().toISOString().split('T')[0];
    const prefix = isMultiInterview ? 'multi-interview' : 'interview';
    const interviewsText = interviewNames.length > 0 ? ` (${interviewNames.join(', ')})` : '';
    
    // Create PDF
    console.log('Creating jsPDF instance...');
    const doc = new jsPDF();
  let yPosition = 20;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  
  // Helper function to add text with automatic page breaks
  const addText = (text: string, fontSize: number = 12, isBold: boolean = false) => {
    if (yPosition > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }
    
    doc.setFontSize(fontSize);
    if (isBold) {
      doc.setFont(undefined, 'bold');
    } else {
      doc.setFont(undefined, 'normal');
    }
    
    const lines = doc.splitTextToSize(text, doc.internal.pageSize.width - 2 * margin);
    doc.text(lines, margin, yPosition);
    yPosition += lines.length * fontSize * 0.5 + 5;
  };
  
  const addBulletPoint = (text: string) => {
    if (yPosition > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }
    
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    const lines = doc.splitTextToSize(`• ${text}`, doc.internal.pageSize.width - 2 * margin - 10);
    doc.text(lines, margin + 10, yPosition);
    yPosition += lines.length * 11 * 0.5 + 3;
  };
  
  // Title and header
  addText('Jobs-to-be-Done Analysis Export', 18, true);
  addText(`Generated on: ${new Date().toLocaleDateString()}`, 12);
  addText(isMultiInterview ? `Interviews analyzed: ${interviewNames.length}${interviewsText}` : 'Single Interview Analysis', 12);
  yPosition += 10;
  
  // JTBD Framework
  addText('JOBS-TO-BE-DONE FRAMEWORK', 16, true);
  yPosition += 5;
  
  addText('Functional Jobs (What users are trying to accomplish)', 14, true);
  jtbdData.functionalJobs.forEach(job => addBulletPoint(job));
  yPosition += 5;
  
  addText('Emotional Jobs (How users want to feel)', 14, true);
  jtbdData.emotionalJobs.forEach(job => addBulletPoint(job));
  yPosition += 5;
  
  addText('Social Jobs (How users want to be perceived)', 14, true);
  if (jtbdData.socialJobs.length > 0) {
    jtbdData.socialJobs.forEach(job => addBulletPoint(job));
  } else {
    addBulletPoint('No specific social jobs identified');
  }
  yPosition += 10;
  
  // Forces
  addText('FORCES AFFECTING BEHAVIOR CHANGE', 16, true);
  yPosition += 5;
  
  addText('Push Forces (Problems with current solution)', 14, true);
  jtbdData.forces.pushForces.forEach(force => addBulletPoint(force));
  yPosition += 5;
  
  addText('Pull Forces (Attraction to new solution)', 14, true);
  jtbdData.forces.pullForces.forEach(force => addBulletPoint(force));
  yPosition += 5;
  
  addText('Anxieties (Concerns about new solution)', 14, true);
  if (jtbdData.forces.anxieties.length > 0) {
    jtbdData.forces.anxieties.forEach(anxiety => addBulletPoint(anxiety));
  } else {
    addBulletPoint('No specific anxieties identified');
  }
  yPosition += 5;
  
  addText('Habits (Current behaviors/inertia)', 14, true);
  if (jtbdData.forces.habits.length > 0) {
    jtbdData.forces.habits.forEach(habit => addBulletPoint(habit));
  } else {
    addBulletPoint('No specific habits identified');
  }
  yPosition += 10;
  
  // Outcomes and Recommendations
  addText('DESIRED OUTCOMES', 16, true);
  jtbdData.outcomes.forEach(outcome => addBulletPoint(outcome));
  yPosition += 10;
  
  addText('STRATEGIC RECOMMENDATIONS', 16, true);
  jtbdData.recommendations.forEach(rec => addBulletPoint(rec));
  yPosition += 20;
  
  // Footer
  addText('Generated by YakStak Multi-Interview Analysis Platform', 10);
  
    // Download the PDF
    console.log('Saving PDF with filename:', `${prefix}-jtbd-analysis-${timestamp}.pdf`);
    doc.save(`${prefix}-jtbd-analysis-${timestamp}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    // Fallback to show error to user
    alert('Failed to generate PDF export. Please try again.');
  }
};